import React, { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  Typography,
  Box,
  Paper,
  Link,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip
} from '@mui/material';
import {
  RestartAlt as RestartAltIcon,
  Restore as RestoreIcon,
  Save as SaveIcon,
  Widgets as WidgetsIcon
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { isBrandedPreset } from '../theme';
import { format } from 'date-fns';
import { parseApiTimestamp, parseLocalDate, formatLocalTodayYmd, formatDateOnly } from '../utils/dateUtils';
import { packingSlipService } from '../services/packingSlipService';
import { customerInvoiceService } from '../services/customerInvoiceService';
import { dashboardService } from '../services/dashboardService';
import { realtimeService } from '../services/realtimeService';
import { formatCurrency } from '../components/CurrencyTextField';
import { useAuth } from '../hooks/useAuth';
import DashboardNotificationsWidget from '../components/dashboard/DashboardNotificationsWidget';
import DashboardEventTable from '../components/dashboard/DashboardEventTable';

const ResponsiveGridLayout = WidthProvider(Responsive);

const DASHBOARD_DRAG_HANDLE_CLASS = 'dashboard-widget-drag';

const DASHBOARD_WIDGET = {
  activity: 'activity',
  notifications: 'notifications',
  quotes: 'quotes',
  orders: 'orders',
  shipments: 'shipments',
  purchaseOrders: 'purchaseOrders',
  received: 'received',
  vendorInvoices: 'vendorInvoices',
  invoices: 'invoices'
};

const DASHBOARD_WIDGET_META = [
  { id: DASHBOARD_WIDGET.activity, label: 'Recent activity' },
  { id: DASHBOARD_WIDGET.notifications, label: 'Notifications', managerOnly: true },
  { id: DASHBOARD_WIDGET.quotes, label: 'Quotes' },
  { id: DASHBOARD_WIDGET.orders, label: 'Orders' },
  { id: DASHBOARD_WIDGET.shipments, label: 'Shipments Out' },
  { id: DASHBOARD_WIDGET.purchaseOrders, label: 'Purchase Orders' },
  { id: DASHBOARD_WIDGET.received, label: 'Shipments in' },
  { id: DASHBOARD_WIDGET.vendorInvoices, label: 'AP Invoices' },
  { id: DASHBOARD_WIDGET.invoices, label: 'AR Invoices' }
];

/** Default: every box on except Notifications. */
const DASHBOARD_DEFAULT_VISIBILITY = {
  [DASHBOARD_WIDGET.activity]: true,
  [DASHBOARD_WIDGET.notifications]: false,
  [DASHBOARD_WIDGET.quotes]: true,
  [DASHBOARD_WIDGET.orders]: true,
  [DASHBOARD_WIDGET.shipments]: true,
  [DASHBOARD_WIDGET.purchaseOrders]: true,
  [DASHBOARD_WIDGET.received]: true,
  [DASHBOARD_WIDGET.vendorInvoices]: true,
  [DASHBOARD_WIDGET.invoices]: true
};

/** Recent activity grid min height (react-grid-layout rows); match other widgets. */
const ACTIVITY_WIDGET_MIN_H = 4;
const ACTIVITY_WIDGET_MIN_H_XXS = 5;

/**
 * Default layouts per breakpoint (cols: lg/md 12, sm 6, xs 4, xxs 2).
 * lg/md: two-column arrangement captured from the saved production layout.
 * Notifications stays off by default in a reserved slot below the two columns.
 */
const DASHBOARD_DEFAULT_LAYOUTS = {
  lg: [
    { i: DASHBOARD_WIDGET.activity, x: 0, y: 0, w: 6, h: 5, minW: 2, minH: ACTIVITY_WIDGET_MIN_H },
    { i: DASHBOARD_WIDGET.quotes, x: 6, y: 0, w: 6, h: 5, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.orders, x: 6, y: 5, w: 6, h: 5, minW: 3, minH: 4 },
    { i: DASHBOARD_WIDGET.notifications, x: 0, y: 18, w: 6, h: 8, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.shipments, x: 6, y: 10, w: 6, h: 4, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.purchaseOrders, x: 0, y: 5, w: 6, h: 4, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.received, x: 0, y: 9, w: 6, h: 5, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.vendorInvoices, x: 0, y: 14, w: 6, h: 4, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.invoices, x: 6, y: 14, w: 6, h: 4, minW: 4, minH: 4 }
  ],
  md: [
    { i: DASHBOARD_WIDGET.activity, x: 0, y: 0, w: 6, h: 5, minW: 2, minH: ACTIVITY_WIDGET_MIN_H },
    { i: DASHBOARD_WIDGET.quotes, x: 6, y: 0, w: 6, h: 5, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.orders, x: 6, y: 5, w: 6, h: 5, minW: 3, minH: 4 },
    { i: DASHBOARD_WIDGET.notifications, x: 0, y: 18, w: 6, h: 8, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.shipments, x: 6, y: 10, w: 6, h: 4, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.purchaseOrders, x: 0, y: 5, w: 6, h: 4, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.received, x: 0, y: 9, w: 6, h: 5, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.vendorInvoices, x: 0, y: 14, w: 6, h: 4, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.invoices, x: 6, y: 14, w: 6, h: 4, minW: 4, minH: 4 }
  ],
  sm: [
    { i: DASHBOARD_WIDGET.activity, x: 0, y: 0, w: 6, h: 16, minW: 2, minH: ACTIVITY_WIDGET_MIN_H },
    { i: DASHBOARD_WIDGET.notifications, x: 0, y: 16, w: 6, h: 6, minW: 3, minH: 4 },
    { i: DASHBOARD_WIDGET.quotes, x: 0, y: 22, w: 6, h: 7, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.orders, x: 0, y: 29, w: 6, h: 7, minW: 3, minH: 4 },
    { i: DASHBOARD_WIDGET.shipments, x: 0, y: 36, w: 6, h: 7, minW: 3, minH: 4 },
    { i: DASHBOARD_WIDGET.purchaseOrders, x: 0, y: 43, w: 6, h: 7, minW: 3, minH: 4 },
    { i: DASHBOARD_WIDGET.received, x: 0, y: 50, w: 6, h: 7, minW: 3, minH: 4 },
    { i: DASHBOARD_WIDGET.vendorInvoices, x: 0, y: 57, w: 6, h: 7, minW: 3, minH: 4 },
    { i: DASHBOARD_WIDGET.invoices, x: 0, y: 64, w: 6, h: 8, minW: 3, minH: 4 }
  ],
  xs: [
    { i: DASHBOARD_WIDGET.activity, x: 0, y: 0, w: 4, h: 16, minW: 2, minH: ACTIVITY_WIDGET_MIN_H },
    { i: DASHBOARD_WIDGET.notifications, x: 0, y: 16, w: 4, h: 6, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.quotes, x: 0, y: 22, w: 4, h: 8, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.orders, x: 0, y: 30, w: 4, h: 8, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.shipments, x: 0, y: 38, w: 4, h: 8, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.purchaseOrders, x: 0, y: 46, w: 4, h: 8, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.received, x: 0, y: 54, w: 4, h: 8, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.vendorInvoices, x: 0, y: 62, w: 4, h: 8, minW: 2, minH: 4 },
    { i: DASHBOARD_WIDGET.invoices, x: 0, y: 70, w: 4, h: 9, minW: 2, minH: 4 }
  ],
  xxs: [
    { i: DASHBOARD_WIDGET.activity, x: 0, y: 0, w: 2, h: 18, minW: 2, minH: ACTIVITY_WIDGET_MIN_H_XXS },
    { i: DASHBOARD_WIDGET.notifications, x: 0, y: 18, w: 2, h: 7, minW: 2, minH: 5 },
    { i: DASHBOARD_WIDGET.quotes, x: 0, y: 25, w: 2, h: 9, minW: 2, minH: 5 },
    { i: DASHBOARD_WIDGET.orders, x: 0, y: 34, w: 2, h: 9, minW: 2, minH: 5 },
    { i: DASHBOARD_WIDGET.shipments, x: 0, y: 43, w: 2, h: 9, minW: 2, minH: 5 },
    { i: DASHBOARD_WIDGET.purchaseOrders, x: 0, y: 52, w: 2, h: 9, minW: 2, minH: 5 },
    { i: DASHBOARD_WIDGET.received, x: 0, y: 61, w: 2, h: 9, minW: 2, minH: 5 },
    { i: DASHBOARD_WIDGET.vendorInvoices, x: 0, y: 70, w: 2, h: 9, minW: 2, minH: 5 },
    { i: DASHBOARD_WIDGET.invoices, x: 0, y: 79, w: 2, h: 10, minW: 2, minH: 5 }
  ]
};

const DASHBOARD_GRID_BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const DASHBOARD_GRID_COLS = { lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 };

function cloneDashboardLayouts(layouts) {
  return JSON.parse(JSON.stringify(layouts));
}

function dashboardLayoutStorageKey(user) {
  const id = user?.id ?? user?.email ?? 'guest';
  return `wm_dashboard_grid_layout_v3:${id}`;
}

function dashboardVisibilityStorageKey(user) {
  const id = user?.id ?? user?.email ?? 'guest';
  return `wm_dashboard_widget_visibility_v1:${id}`;
}

function dashboardSavedLayoutStorageKey(user) {
  const id = user?.id ?? user?.email ?? 'guest';
  return `wm_dashboard_grid_saved_v1:${id}`;
}

function mergeDashboardLayouts(saved, defaults) {
  if (!saved || typeof saved !== 'object') {
    return defaults;
  }
  const out = {};
  Object.keys(defaults).forEach((bp) => {
    const defItems = defaults[bp];
    const savItems = Array.isArray(saved[bp]) ? saved[bp] : [];
    const savMap = new Map(savItems.map((item) => [item.i, item]));
    out[bp] = defItems.map((d) => {
      const s = savMap.get(d.i);
      if (!s) {
        return { ...d };
      }
      return {
        ...d,
        x: s.x,
        y: s.y,
        w: s.w,
        h: s.h
      };
    });
  });
  return out;
}

function loadDashboardLayouts(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return cloneDashboardLayouts(DASHBOARD_DEFAULT_LAYOUTS);
    }
    const parsed = JSON.parse(raw);
    return mergeDashboardLayouts(parsed, DASHBOARD_DEFAULT_LAYOUTS);
  } catch {
    return cloneDashboardLayouts(DASHBOARD_DEFAULT_LAYOUTS);
  }
}

function writeDashboardLayouts(storageKey, nextLayouts) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(nextLayouts));
  } catch {
    // ignore quota / private mode
  }
}

function hasSavedDashboardLayouts(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return false;
    }
    const parsed = JSON.parse(raw);
    return Boolean(parsed && typeof parsed === 'object');
  } catch {
    return false;
  }
}

function loadDashboardVisibility(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return { ...DASHBOARD_DEFAULT_VISIBILITY };
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return { ...DASHBOARD_DEFAULT_VISIBILITY };
    }
    return {
      ...DASHBOARD_DEFAULT_VISIBILITY,
      ...Object.fromEntries(
        Object.keys(DASHBOARD_DEFAULT_VISIBILITY).map((id) => [
          id,
          parsed[id] === undefined ? DASHBOARD_DEFAULT_VISIBILITY[id] : Boolean(parsed[id])
        ])
      )
    };
  } catch {
    return { ...DASHBOARD_DEFAULT_VISIBILITY };
  }
}

function saveDashboardVisibility(storageKey, visibility) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(visibility));
  } catch {
    // ignore quota / private mode
  }
}

/**
 * One-shot pack when the Notifications widget is toggled on (lg/md only).
 * Do not call on every render — that overrides user drag/resize of Quotes/Orders.
 *
 * If Quotes+Orders still look like a shared half/half row, shrink them to make room
 * for Notifications on the right. Otherwise leave the two-column default alone.
 */
function packNotificationsIntoQuotesOrdersRow(layouts) {
  const out = {};
  Object.entries(layouts).forEach(([bp, items]) => {
    const wideRow = bp === 'lg' || bp === 'md';
    if (!wideRow) {
      out[bp] = items.map((item) => ({ ...item }));
      return;
    }

    const quotes = items.find((x) => x.i === DASHBOARD_WIDGET.quotes);
    const orders = items.find((x) => x.i === DASHBOARD_WIDGET.orders);
    const looksLikeSharedHalfRow =
      quotes &&
      orders &&
      quotes.x === 0 &&
      quotes.w === 6 &&
      orders.x === 6 &&
      orders.w === 6 &&
      quotes.y === orders.y;

    if (!looksLikeSharedHalfRow) {
      out[bp] = items.map((item) => ({ ...item }));
      return;
    }

    out[bp] = items.map((item) => {
      if (item.i === DASHBOARD_WIDGET.quotes) {
        return { ...item, x: 0, w: 5 };
      }
      if (item.i === DASHBOARD_WIDGET.orders) {
        return { ...item, x: 5, w: 4 };
      }
      if (item.i === DASHBOARD_WIDGET.notifications) {
        return { ...item, x: 9, y: quotes.y, w: 3, h: quotes.h };
      }
      return { ...item };
    });
  });
  return out;
}

function filterLayoutsByVisibility(layouts, visibility) {
  const out = {};
  Object.entries(layouts).forEach(([bp, items]) => {
    out[bp] = items.filter((item) => visibility[item.i]);
  });
  return out;
}

/** Merge drag/resize updates for visible widgets back into the full layout (keeps hidden widget positions). */
function mergeVisibleLayoutsIntoFull(fullLayouts, nextVisibleLayouts, visibility) {
  const out = {};
  Object.keys(DASHBOARD_DEFAULT_LAYOUTS).forEach((bp) => {
    const fullItems = Array.isArray(fullLayouts[bp]) ? fullLayouts[bp] : DASHBOARD_DEFAULT_LAYOUTS[bp];
    const nextItems = Array.isArray(nextVisibleLayouts?.[bp]) ? nextVisibleLayouts[bp] : [];
    const nextMap = new Map(nextItems.map((item) => [item.i, item]));
    out[bp] = fullItems.map((item) => {
      if (!visibility[item.i]) {
        return { ...item };
      }
      const next = nextMap.get(item.i);
      if (!next) {
        return { ...item };
      }
      return {
        ...item,
        x: next.x,
        y: next.y,
        w: next.w,
        h: next.h
      };
    });
  });
  return out;
}

const customerName = (row) =>
  row?.company_name || [row?.customer_first_name, row?.customer_last_name].filter(Boolean).join(' ') || row?.customer_name || '-';

const RECONCILE_INTERVAL_MS = 60000;
const POLL_INTERVAL_MS = 3000;
const DASHBOARD_REALTIME_ENABLED = process.env.REACT_APP_DASHBOARD_REALTIME_ENABLED !== 'false';
const CLOCK_TICK_MS = 1000;
const MAX_ORDER_ROWS = 18;
const MAX_QUOTE_ROWS = 18;
const MAX_SHIPMENT_ROWS = 18;
const MAX_PO_ROWS = 14;
const MAX_RECEIVED_ROWS = 14;
const MAX_VENDOR_INVOICE_ROWS = 14;
const MAX_INVOICE_ROWS = 16;
/** Recent activity: rows requested from `/api/dashboard/activity-feed` (server max 100). */
const MAX_ACTIVITY_ROWS = 100;

/** API list responses are usually { data: T[] }; tolerate arrays or missing data. */
const listFromResponse = (body) => {
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  return [];
};

/**
 * Widget title bars stay a solid fill in both themes; only the color changes.
 * Fab Works fills with steel so headers read as part of the industrial palette.
 */
const buildDashboardPalette = (theme) => {
  const tokens = theme.fw?.tokens;
  if (isBrandedPreset(theme.fw?.preset) && tokens) {
    const lightNav = Boolean(theme.fw?.lightNav);
    const brandAccent = tokens.accent || tokens.black || tokens.ink;
    return {
      accent: lightNav ? brandAccent : tokens.steelDark,
      headerBg: lightNav ? brandAccent : tokens.steel,
      headerText: tokens.white,
      showHeaderAccent: !lightNav,
      hoverBg: lightNav ? tokens.subtle : alpha(tokens.steel, 0.1),
      rowAltBg: lightNav ? tokens.subtle : alpha(tokens.steel, 0.05),
    };
  }
  const primary = theme.palette.primary.main;
  return {
    accent: primary,
    headerBg: primary,
    headerText: '#FFFFFF',
    hoverBg: alpha(primary, 0.12),
    rowAltBg: alpha(primary, 0.06),
  };
};

const formatEventDate = (dateString) => {
  if (!dateString) return '--/-- --:--';
  try {
    const dt = parseApiTimestamp(dateString);
    if (!dt) return '--/-- --:--';
    if (Number.isNaN(dt.getTime())) return '--/-- --:--';
    return format(dt, 'M/d');
  } catch (err) {
    return '--/--';
  }
};

const formatEventTime = (dateString) => {
  if (!dateString) return '--:--';
  try {
    const dt = parseApiTimestamp(dateString);
    if (!dt) return '--:--';
    if (Number.isNaN(dt.getTime())) return '--:--';
    return format(dt, 'h:mma').toLowerCase();
  } catch (err) {
    return '--:--';
  }
};

/** Align with backend `dashboardActivityFeed` packing slip wording (create line). */
const packingSlipKindShortWords = (s) =>
  String(s?.shipment_purpose || '').toLowerCase() === 'vendor_outbound' ? 'Vend Shipment' : 'Cust Shipment';

const slipDashboardLabel = (s) => s?.identifier || s?.packing_slip_number || (s?.id != null ? `#${s.id}` : '—');

const shipmentWoLabel = (s) => {
  if (!s) return '';
  if (s.work_order_identifier != null && String(s.work_order_identifier).trim() !== '') {
    return String(s.work_order_identifier).trim();
  }
  if (s.work_order_number != null && String(s.work_order_number).trim() !== '') {
    return String(s.work_order_number).trim();
  }
  if (s.work_order_id != null) return `#${s.work_order_id}`;
  return '';
};

const shipmentCreatorName = (s) => {
  const a = [s?.created_by_first_name, s?.created_by_last_name].filter(Boolean).join(' ').trim();
  return a || '—';
};

const shipmentCustomerDisplay = (s) => s?.customer_name || '—';

const shipmentLineLinkSx = { fontWeight: 600, color: 'inherit', fontSize: 'inherit' };

const quoteLineLinkSx = { fontWeight: 600, color: 'inherit', fontSize: 'inherit' };

const renderDashboardQuoteActivityLine = (item) => {
  const label = item?.identifier || (item?.quote_id != null ? `#${item.quote_id}` : '—');
  const actor = item?.actor || '—';
  const detail = ` - ${item?.customer || '—'}, ${formatCurrency(item?.total_amount)} (${actor})`;

  return (
    <>
      <Link
        component={RouterLink}
        to={`/quotes/${item.quote_id}`}
        underline="hover"
        onClick={(e) => e.stopPropagation()}
        sx={quoteLineLinkSx}
      >
        {label}
      </Link>
      {`: ${item?.verb || 'Quote updated'}${detail}`}
    </>
  );
};

const orderLineLinkSx = { fontWeight: 600, color: 'inherit', fontSize: 'inherit' };

const renderDashboardOrderActivityLine = (item) => {
  const label = item?.identifier || (item?.work_order_id != null ? `#${item.work_order_id}` : '—');
  const actor = item?.actor || '—';
  const emphasis =
    item?.emphasis_text ||
    `${item?.customer || '—'}, ${formatCurrency(item?.total_amount)}`;
  const statusPart = item?.status_change
    ? ` (status ${item.status_change.from} → ${item.status_change.to})`
    : item?.status_label
      ? ` ${item.status_label}`
      : '';

  return (
    <>
      <Link
        component={RouterLink}
        to={`/work-orders/${item.work_order_id}`}
        underline="hover"
        onClick={(e) => e.stopPropagation()}
        sx={orderLineLinkSx}
      >
        {label}
      </Link>
      {`: ${item?.verb || 'Order updated'}. `}
      <Box component="span" sx={{ fontStyle: 'italic' }}>
        {emphasis}
      </Box>
      {`${statusPart} (${actor})`}
    </>
  );
};

const poLineLinkSx = { fontWeight: 600, color: 'inherit', fontSize: 'inherit' };

const renderDashboardPurchaseOrderActivityLine = (item) => {
  const label = item?.identifier || (item?.purchase_order_id != null ? `#${item.purchase_order_id}` : '—');
  const actor = item?.actor || '—';
  const emphasis =
    item?.emphasis_text ||
    `${item?.vendor || '—'}, ${formatCurrency(item?.total_amount)}`;

  return (
    <>
      <Link
        component={RouterLink}
        to={`/procurement/po/${item.purchase_order_id}`}
        underline="hover"
        onClick={(e) => e.stopPropagation()}
        sx={poLineLinkSx}
      >
        {label}
      </Link>
      {`: ${item?.verb || 'PO Updated'}. `}
      <Box component="span" sx={{ fontStyle: 'italic' }}>
        {emphasis}
      </Box>
      {` (${actor})`}
    </>
  );
};

const shipmentInLineLinkSx = { fontWeight: 600, color: 'inherit', fontSize: 'inherit' };

const renderDashboardShipmentInActivityLine = (item) => {
  const label = item?.identifier || (item?.shipment_in_id != null ? `#${item.shipment_in_id}` : '—');
  const actor = item?.actor || '—';
  const poLabel = item?.po_identifier;
  const poSeg =
    poLabel != null && String(poLabel).trim() !== '' ? (
      <>
        {' for PO '}
        {item.purchase_order_id ? (
          <Link
            component={RouterLink}
            to={`/procurement/po/${item.purchase_order_id}`}
            underline="hover"
            onClick={(e) => e.stopPropagation()}
            sx={shipmentInLineLinkSx}
          >
            {poLabel}
          </Link>
        ) : (
          poLabel
        )}
      </>
    ) : null;

  return (
    <>
      <Link
        component={RouterLink}
        to={`/procurement/shipments-in/${item.shipment_in_id}`}
        underline="hover"
        onClick={(e) => e.stopPropagation()}
        sx={shipmentInLineLinkSx}
      >
        {label}
      </Link>
      {`: ${item?.verb || 'Shipment Received'}`}
      {poSeg}
      {', '}
      <Box component="span" sx={{ fontStyle: 'italic' }}>
        {item?.vendor || '—'}
      </Box>
      {` (${actor})`}
    </>
  );
};

const vendorInvoiceLineLinkSx = { fontWeight: 600, color: 'inherit', fontSize: 'inherit' };

function dashboardApInvoiceIsDueToday(inv) {
  const due = parseLocalDate(inv?.due_date);
  if (!due) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return due.getTime() === today.getTime();
}

function dashboardApInvoiceIsPastDue(inv) {
  const due = parseLocalDate(inv?.due_date);
  if (!due) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return due.getTime() < today.getTime();
}

/**
 * AP invoice status chip for the dashboard (aligned with Vendor Invoices page).
 * No due date → Open; with due date → Due MM/dd/yyyy (Past Due / Due today when applicable).
 */
function dashboardApInvoiceStatusChip(inv) {
  const status = String(inv?.status || '').toLowerCase();
  if (status === 'paid') {
    return { label: 'Paid', sx: { bgcolor: 'success.main', color: '#fff' } };
  }
  if (status === 'closed') {
    return { label: 'Closed', sx: { bgcolor: 'grey.600', color: '#fff' } };
  }
  if (status === 'open' || !status) {
    if (!inv?.due_date) {
      return { label: 'Open', sx: { bgcolor: 'info.main', color: '#fff' } };
    }
    const dueLabel = formatDateOnly(inv.due_date, 'MM/dd/yyyy') || '—';
    if (dashboardApInvoiceIsPastDue(inv)) {
      return { label: `Past Due ${dueLabel}`, sx: { bgcolor: 'error.main', color: '#fff' } };
    }
    if (dashboardApInvoiceIsDueToday(inv)) {
      return { label: `Due today ${dueLabel}`, sx: { bgcolor: '#fdd835', color: '#111' } };
    }
    return { label: `Due ${dueLabel}`, sx: { bgcolor: 'info.main', color: '#fff' } };
  }
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return { label, sx: { bgcolor: 'grey.500', color: '#fff' } };
}

function renderDashboardApInvoiceStatusChip(inv) {
  const main = dashboardApInvoiceStatusChip(inv);
  return (
    <Chip
      label={main.label}
      size="small"
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        height: 20,
        fontSize: '0.65rem',
        flexShrink: 0,
        '& .MuiChip-label': { px: 0.6 },
        ...main.sx
      }}
    />
  );
}

const renderDashboardVendorInvoiceActivityLine = (item) => {
  const label =
    item?.identifier || (item?.vendor_invoice_id != null ? `#${item.vendor_invoice_id}` : '—');
  const actor = item?.actor || '—';
  const emphasis =
    item?.emphasis_text ||
    (item?.total_amount != null ? formatCurrency(item.total_amount) : '—');
  const poLabel = item?.po_identifier;
  const poSeg =
    poLabel != null && String(poLabel).trim() !== '' ? (
      <>
        PO{' '}
        {item.purchase_order_id ? (
          <Link
            component={RouterLink}
            to={`/procurement/po/${item.purchase_order_id}`}
            underline="hover"
            onClick={(e) => e.stopPropagation()}
            sx={vendorInvoiceLineLinkSx}
          >
            {poLabel}
          </Link>
        ) : (
          poLabel
        )}
        {', '}
      </>
    ) : null;

  return (
    <>
      <Link
        component={RouterLink}
        to={`/procurement/invoices?invoice=${item.vendor_invoice_id}`}
        underline="hover"
        onClick={(e) => e.stopPropagation()}
        sx={vendorInvoiceLineLinkSx}
      >
        {label}
      </Link>
      {': Invoice for '}
      {poSeg}
      <Box component="span" sx={{ fontStyle: 'italic' }}>
        {emphasis}
      </Box>
      {` (${actor})`}
    </>
  );
};

/** Same phrasing as Recent activity packing-slip create rows (Shipments list is not an audit log). */
const renderDashboardShipmentLine = (s) => {
  const label = slipDashboardLabel(s);
  const cust = shipmentCustomerDisplay(s);
  const slipTo = `/packing-slips/${s.id}`;

  const kindShort = packingSlipKindShortWords(s);
  const woLbl = shipmentWoLabel(s);
  const orderSeg = woLbl !== '' && woLbl !== '—';
  const actor = shipmentCreatorName(s);

  return (
    <>
      <Link
        component={RouterLink}
        to={slipTo}
        underline="hover"
        onClick={(e) => e.stopPropagation()}
        sx={shipmentLineLinkSx}
      >
        {label}
      </Link>
      {`: ${kindShort}`}
      {orderSeg ? (
        <>
          {' for Order '}
          {s.work_order_id ? (
            <Link
              component={RouterLink}
              to={`/work-orders/${s.work_order_id}`}
              underline="hover"
              onClick={(e) => e.stopPropagation()}
              sx={shipmentLineLinkSx}
            >
              {woLbl}
            </Link>
          ) : (
            woLbl
          )}
        </>
      ) : null}
      {` being prepared for ${cust} (${actor})`}
    </>
  );
};

/** Activity feed: "04/08 11:23am" in local time */
const formatActivityStamp = (dateInput) => {
  if (dateInput == null || dateInput === '') return '--/-- --:--';
  try {
    const dt =
      dateInput instanceof Date
        ? (Number.isNaN(dateInput.getTime()) ? null : dateInput)
        : parseApiTimestamp(dateInput);
    if (!dt || Number.isNaN(dt.getTime())) return '--/-- --:--';
    return format(dt, 'MM/dd hh:mma').toLowerCase();
  } catch {
    return '--/-- --:--';
  }
};

/** Split a slice of `message` into plain / italic segments using optional `emphasisSpan` (global indices). */
const activityMessageChunksWithEmphasis = (message, from, to, emphasisSpan) => {
  if (to <= from) return [];
  const emp = emphasisSpan;
  if (!emp || emp.end <= from || emp.start >= to) {
    return [{ type: 'text', text: message.slice(from, to) }];
  }
  const oS = Math.max(from, emp.start);
  const oE = Math.min(to, emp.end);
  const chunks = [];
  if (from < oS) chunks.push({ type: 'text', text: message.slice(from, oS) });
  if (oS < oE) chunks.push({ type: 'em', text: message.slice(oS, oE) });
  if (oE < to) chunks.push({ type: 'text', text: message.slice(oE, to) });
  return chunks;
};

/** Turn activity `message` into text + in-app links using server-provided `links` (label substrings). */
const renderActivityFeedMessage = (message, links, emphasisSpan) => {
  if (message == null || message === '') return '—';

  const pushEmphasisChunks = (out, from, to, keyBase) => {
    activityMessageChunksWithEmphasis(message, from, to, emphasisSpan).forEach((seg, si) => {
      if (seg.type === 'em' && seg.text) {
        out.push(
          <Box component="span" key={`${keyBase}-em-${si}`} sx={{ fontStyle: 'italic' }}>
            {seg.text}
          </Box>
        );
      } else if (seg.type === 'text' && seg.text) {
        out.push(<Fragment key={`${keyBase}-tx-${si}`}>{seg.text}</Fragment>);
      }
    });
  };

  if (!Array.isArray(links) || links.length === 0) {
    const out = [];
    pushEmphasisChunks(out, 0, message.length, 'solo');
    return out.length ? out : message;
  }

  const markers = links
    .filter((l) => l && l.label && l.to)
    .map((l) => ({ ...l, idx: message.indexOf(l.label) }))
    .filter((l) => l.idx !== -1 && String(l.label).length > 0)
    .sort((a, b) => a.idx - b.idx);

  if (!markers.length) {
    const out = [];
    pushEmphasisChunks(out, 0, message.length, 'nolink');
    return out.length ? out : message;
  }

  const out = [];
  let cursor = 0;
  markers.forEach((m, n) => {
    if (m.idx < cursor) return;
    if (m.idx > cursor) {
      pushEmphasisChunks(out, cursor, m.idx, `t-${m.idx}-${n}`);
    }
    out.push(
      <Link
        key={`l-${m.to}-${m.idx}-${n}`}
        component={RouterLink}
        to={m.to}
        underline="hover"
        sx={{ fontWeight: 600, color: 'inherit', fontSize: 'inherit' }}
      >
        {m.label}
      </Link>
    );
    cursor = m.idx + m.label.length;
  });
  if (cursor < message.length) {
    pushEmphasisChunks(out, cursor, message.length, `e-${cursor}`);
  }
  return out;
};

const IdentifierLink = ({ label, to, navigate }) => {
  if (!label || label === '-' || !to) {
    return <>{label || '-'}</>;
  }

  return (
    <Link
      component="button"
      type="button"
      underline="always"
      color="inherit"
      onClick={(e) => {
        e.stopPropagation();
        navigate(to);
      }}
      sx={{
        fontSize: 'inherit',
        lineHeight: 'inherit',
        verticalAlign: 'baseline',
        p: 0,
        minWidth: 0,
        fontWeight: 700,
        textAlign: 'left'
      }}
    >
      {label}
    </Link>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const dashboardPalette = useMemo(() => buildDashboardPalette(theme), [theme]);
  const navigate = useNavigate();
  const { user, isManagerOrAdmin } = useAuth();
  const isManager = typeof isManagerOrAdmin === 'function' ? isManagerOrAdmin() : false;
  const layoutStorageKey = useMemo(() => dashboardLayoutStorageKey(user), [user]);
  const visibilityStorageKey = useMemo(() => dashboardVisibilityStorageKey(user), [user]);
  const savedLayoutStorageKey = useMemo(() => dashboardSavedLayoutStorageKey(user), [user]);
  const [layouts, setLayouts] = useState(() => loadDashboardLayouts(dashboardLayoutStorageKey(null)));
  const [widgetVisibility, setWidgetVisibility] = useState(() =>
    loadDashboardVisibility(dashboardVisibilityStorageKey(null))
  );
  const [hasSavedLayout, setHasSavedLayout] = useState(() =>
    hasSavedDashboardLayouts(dashboardSavedLayoutStorageKey(null))
  );
  const [layoutSaveFlash, setLayoutSaveFlash] = useState(false);
  const [widgetsMenuAnchor, setWidgetsMenuAnchor] = useState(null);
  const [layoutResetNonce, setLayoutResetNonce] = useState(0);
  const layoutSaveTimerRef = useRef(null);

  const effectiveVisibility = useMemo(() => {
    if (isManager) return widgetVisibility;
    return { ...widgetVisibility, [DASHBOARD_WIDGET.notifications]: false };
  }, [widgetVisibility, isManager]);

  const gridLayouts = useMemo(
    () => filterLayoutsByVisibility(layouts, effectiveVisibility),
    [layouts, effectiveVisibility]
  );

  const toggleableWidgets = useMemo(
    () => DASHBOARD_WIDGET_META.filter((w) => !w.managerOnly || isManager),
    [isManager]
  );

  const [loading, setLoading] = useState(true);
  const [clockNow, setClockNow] = useState(new Date());
  const [recentOrderActivity, setRecentOrderActivity] = useState([]);
  const [recentQuoteActivity, setRecentQuoteActivity] = useState([]);
  const [recentShipments, setRecentShipments] = useState([]);
  const [recentPurchaseOrderActivity, setRecentPurchaseOrderActivity] = useState([]);
  const [recentReceivedShipmentActivity, setRecentReceivedShipmentActivity] = useState([]);
  const [recentVendorInvoiceActivity, setRecentVendorInvoiceActivity] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [loadError, setLoadError] = useState('');

  const loadDashboard = useCallback(async ({ isBackground = false } = {}) => {
    try {
      if (!isBackground) {
        setLoading(true);
      }

      const settled = await Promise.allSettled([
        dashboardService.getOrderActivity({ limit: MAX_ORDER_ROWS }),
        dashboardService.getQuoteActivity({ limit: MAX_QUOTE_ROWS }),
        packingSlipService.getAll({ limit: MAX_SHIPMENT_ROWS }),
        dashboardService.getPurchaseOrderActivity({ limit: MAX_PO_ROWS }),
        dashboardService.getShipmentInActivity({ limit: MAX_RECEIVED_ROWS }),
        dashboardService.getVendorInvoiceActivity({ limit: MAX_VENDOR_INVOICE_ROWS }),
        customerInvoiceService.getAll({ limit: MAX_INVOICE_ROWS }),
        dashboardService.getActivityFeed({ limit: MAX_ACTIVITY_ROWS })
      ]);

      const takeList = (i) => {
        const r = settled[i];
        if (r.status === 'fulfilled') return listFromResponse(r.value);
        const msg =
          r.reason?.response?.data?.message ||
          r.reason?.message ||
          (typeof r.reason === 'string' ? r.reason : 'Request failed');
        console.error('Dashboard partial load error:', msg, r.reason);
        return { __error: msg };
      };

      const orderActivity = takeList(0);
      const quoteActivity = takeList(1);
      const slips = takeList(2);
      const poActivity = takeList(3);
      const receivingActivity = takeList(4);
      const vendorInvoiceActivity = takeList(5);
      const invoices = takeList(6);
      const activity = takeList(7);
      const failedMessages = [];
      [
        orderActivity,
        quoteActivity,
        slips,
        poActivity,
        receivingActivity,
        vendorInvoiceActivity,
        invoices,
        activity
      ].forEach((x) => {
        if (x && x.__error) failedMessages.push(x.__error);
      });

      setRecentOrderActivity(
        (Array.isArray(orderActivity) ? orderActivity : []).slice(0, MAX_ORDER_ROWS)
      );

      setRecentQuoteActivity(
        (Array.isArray(quoteActivity) ? quoteActivity : []).slice(0, MAX_QUOTE_ROWS)
      );
      setRecentShipments((Array.isArray(slips) ? slips : []).slice(0, MAX_SHIPMENT_ROWS));
      setRecentPurchaseOrderActivity(
        (Array.isArray(poActivity) ? poActivity : []).slice(0, MAX_PO_ROWS)
      );
      setRecentReceivedShipmentActivity(
        (Array.isArray(receivingActivity) ? receivingActivity : []).slice(0, MAX_RECEIVED_ROWS)
      );
      setRecentVendorInvoiceActivity(
        (Array.isArray(vendorInvoiceActivity) ? vendorInvoiceActivity : []).slice(0, MAX_VENDOR_INVOICE_ROWS)
      );
      setRecentInvoices((Array.isArray(invoices) ? invoices : []).slice(0, MAX_INVOICE_ROWS));

      if (activity && !activity.__error) {
        setActivityFeed(listFromResponse(activity).slice(0, MAX_ACTIVITY_ROWS));
      } else {
        setActivityFeed([]);
      }

      const uniqueErrs = [...new Set(failedMessages.filter(Boolean))];
      if (uniqueErrs.length > 0) {
        setLoadError(
          uniqueErrs.length === 1
            ? uniqueErrs[0]
            : `${uniqueErrs.length} requests failed (e.g. ${uniqueErrs[0]}). Open the browser console for details.`
        );
      } else {
        setLoadError('');
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
      setLoadError(err?.response?.data?.message || err?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    let unsubscribeEvents = () => {};
    let unsubscribeConnection = () => {};
    let debounceTimerId = null;
    let intervalId = null;

    if (DASHBOARD_REALTIME_ENABLED) {
      realtimeService.setSubscriptions(['quote', 'work_order', 'packing_slip', 'purchase_order', 'receiving', 'vendor_invoice', 'customer_invoice']);
      realtimeService.connect();
      const seenEventIds = new Set();
      const triggerRefresh = () => {
        if (debounceTimerId) {
          window.clearTimeout(debounceTimerId);
        }
        debounceTimerId = window.setTimeout(() => {
          loadDashboard({ isBackground: true });
        }, 300);
      };

      unsubscribeEvents = realtimeService.subscribe((event) => {
        if (event?.eventId) {
          if (seenEventIds.has(event.eventId)) return;
          if (seenEventIds.size > 200) {
            const first = seenEventIds.values().next().value;
            seenEventIds.delete(first);
          }
          seenEventIds.add(event.eventId);
        }

        const trackedEntities = new Set(['quote', 'work_order', 'packing_slip', 'purchase_order', 'receiving', 'vendor_invoice', 'customer_invoice']);
        if (trackedEntities.has(event?.entity)) {
          triggerRefresh();
        }
      });

      unsubscribeConnection = realtimeService.onConnectionStateChange((state) => {
        setConnectionState(state);
      });

      intervalId = window.setInterval(() => {
        loadDashboard({ isBackground: true });
      }, RECONCILE_INTERVAL_MS);
    } else {
      setConnectionState('polling_only');
      intervalId = window.setInterval(() => {
        loadDashboard({ isBackground: true });
      }, POLL_INTERVAL_MS);
    }

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
      if (debounceTimerId) {
        window.clearTimeout(debounceTimerId);
      }
      unsubscribeEvents();
      unsubscribeConnection();
      if (DASHBOARD_REALTIME_ENABLED) {
        realtimeService.setSubscriptions([]);
        realtimeService.disconnect();
      }
    };
  }, [loadDashboard]);

  useEffect(() => {
    setLayouts(loadDashboardLayouts(layoutStorageKey));
    setWidgetVisibility(loadDashboardVisibility(visibilityStorageKey));
    setHasSavedLayout(hasSavedDashboardLayouts(savedLayoutStorageKey));
    setLayoutSaveFlash(false);
  }, [layoutStorageKey, visibilityStorageKey, savedLayoutStorageKey]);

  useEffect(
    () => () => {
      if (layoutSaveTimerRef.current) {
        window.clearTimeout(layoutSaveTimerRef.current);
      }
    },
    []
  );

  const persistLayouts = useCallback(
    (nextLayouts) => {
      if (layoutSaveTimerRef.current) {
        window.clearTimeout(layoutSaveTimerRef.current);
      }
      layoutSaveTimerRef.current = window.setTimeout(() => {
        writeDashboardLayouts(layoutStorageKey, nextLayouts);
      }, 400);
    },
    [layoutStorageKey]
  );

  const handleDashboardLayoutChange = useCallback(
    (_layout, nextLayouts) => {
      setLayouts((prev) => {
        const merged = mergeVisibleLayoutsIntoFull(prev, nextLayouts, effectiveVisibility);
        persistLayouts(merged);
        return merged;
      });
    },
    [effectiveVisibility, persistLayouts]
  );

  const applyDashboardLayouts = useCallback(
    (nextLayouts) => {
      if (layoutSaveTimerRef.current) {
        window.clearTimeout(layoutSaveTimerRef.current);
        layoutSaveTimerRef.current = null;
      }
      setLayouts(nextLayouts);
      writeDashboardLayouts(layoutStorageKey, nextLayouts);
      setLayoutResetNonce((n) => n + 1);
    },
    [layoutStorageKey]
  );

  const handleSaveDashboardLayout = useCallback(() => {
    writeDashboardLayouts(savedLayoutStorageKey, cloneDashboardLayouts(layouts));
    setHasSavedLayout(true);
    setLayoutSaveFlash(true);
  }, [layouts, savedLayoutStorageKey]);

  const handleRestoreSavedDashboardLayout = useCallback(() => {
    if (!hasSavedDashboardLayouts(savedLayoutStorageKey)) {
      return;
    }
    applyDashboardLayouts(loadDashboardLayouts(savedLayoutStorageKey));
    setWidgetsMenuAnchor(null);
  }, [applyDashboardLayouts, savedLayoutStorageKey]);

  const handleResetDashboardLayout = useCallback(() => {
    let next = cloneDashboardLayouts(DASHBOARD_DEFAULT_LAYOUTS);
    if (effectiveVisibility[DASHBOARD_WIDGET.notifications]) {
      next = packNotificationsIntoQuotesOrdersRow(next);
    }
    applyDashboardLayouts(next);
    setWidgetsMenuAnchor(null);
  }, [applyDashboardLayouts, effectiveVisibility]);

  const handleToggleWidget = useCallback(
    (widgetId, nextVisible) => {
      setWidgetVisibility((prev) => {
        const next = { ...prev, [widgetId]: nextVisible };
        if (!isManager) {
          next[DASHBOARD_WIDGET.notifications] = false;
        }
        saveDashboardVisibility(visibilityStorageKey, next);
        return next;
      });

      if (widgetId === DASHBOARD_WIDGET.notifications && nextVisible) {
        setLayouts((prev) => {
          const packed = packNotificationsIntoQuotesOrdersRow(prev);
          persistLayouts(packed);
          return packed;
        });
      }
    },
    [isManager, visibilityStorageKey, persistLayouts]
  );

  const invoiceBalanceDue = (inv) => {
    const total = Number(inv?.total_amount) || 0;
    const paid = Number(inv?.amount_paid) || 0;
    return Math.max(0, total - paid);
  };

  const invoiceIsPartial = (inv) => {
    const total = Number(inv?.total_amount) || 0;
    const paid = Number(inv?.amount_paid) || 0;
    return total > 0 && paid > 0.02 && paid < total - 0.02;
  };

  const invoiceIsDueToday = (inv) => {
    const due = parseLocalDate(inv?.due_date);
    if (!due) return false;
    const today = parseLocalDate(formatLocalTodayYmd());
    if (!today) return false;
    due.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return due.getTime() === today.getTime();
  };

  const invoiceStatusChipData = (inv) => {
    const status = String(inv?.status || '');
    if (status === 'Paid') return { label: 'Paid', sx: { bgcolor: 'success.main', color: '#fff' } };
    if (status === 'Past Due') return { label: 'Past Due', sx: { bgcolor: 'error.main', color: '#fff' } };
    if (invoiceIsDueToday(inv)) return { label: 'Due today', sx: { bgcolor: 'warning.main', color: '#111' } };
    return { label: 'Due', sx: { bgcolor: 'info.main', color: '#fff' } };
  };

  const renderInvoiceStatusChips = (inv) => {
    const main = invoiceStatusChipData(inv);
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
        <Chip label={main.label} size="small" sx={{ fontWeight: 600, height: 22, ...main.sx }} />
        {invoiceIsPartial(inv) ? (
          <Chip
            label="Partial"
            size="small"
            sx={{ backgroundColor: '#ed6c02', color: '#fff', fontWeight: 600, height: 22 }}
          />
        ) : null}
      </Box>
    );
  };

  useEffect(() => {
    const tickId = window.setInterval(() => {
      setClockNow(new Date());
    }, CLOCK_TICK_MS);
    return () => window.clearInterval(tickId);
  }, []);

  return (
    <Box
      className="no-print-dashboard"
      sx={{
        height: 'calc(100vh - 72px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        gap: 1
      }}
    >
      <Paper sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                color: theme.fw?.lightNav ? 'text.primary' : 'primary.dark'
              }}
            >
              {theme.fw?.lightNav ? 'Dashboard' : 'Warehouse Mission Control'}
            </Typography>
            {loadError && (
              <Alert severity="warning" sx={{ mt: 1, py: 0.25 }} onClose={() => setLoadError('')}>
                {loadError}
                {' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  Other sections may still show data. If you pulled new code, run pending database migrations.
                </Typography>
              </Alert>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Tooltip title="Show or hide dashboard boxes">
              <IconButton
                size="small"
                aria-label="Show or hide dashboard boxes"
                aria-controls={widgetsMenuAnchor ? 'dashboard-widgets-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={Boolean(widgetsMenuAnchor)}
                onClick={(e) => setWidgetsMenuAnchor(e.currentTarget)}
              >
                <WidgetsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              id="dashboard-widgets-menu"
              anchorEl={widgetsMenuAnchor}
              open={Boolean(widgetsMenuAnchor)}
              onClose={() => {
                setWidgetsMenuAnchor(null);
                setLayoutSaveFlash(false);
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem disabled sx={{ opacity: 1, py: 0.5 }}>
                <ListItemText
                  primary="Dashboard boxes"
                  primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 700 }}
                />
              </MenuItem>
              <Divider />
              {toggleableWidgets.map((widget) => (
                <MenuItem
                  key={widget.id}
                  dense
                  onClick={() => handleToggleWidget(widget.id, !effectiveVisibility[widget.id])}
                  sx={{ py: 0.25 }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={Boolean(effectiveVisibility[widget.id])}
                        onChange={(e) => handleToggleWidget(widget.id, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    label={widget.label}
                    sx={{ m: 0, width: '100%', pointerEvents: 'none' }}
                  />
                </MenuItem>
              ))}
              <Divider />
              <MenuItem dense onClick={handleSaveDashboardLayout} sx={{ py: 0.75 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <SaveIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Save current layout"
                  secondary={layoutSaveFlash ? 'Saved' : 'Remember these widget positions'}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    color: layoutSaveFlash ? 'success.main' : 'text.secondary'
                  }}
                />
              </MenuItem>
              <MenuItem
                dense
                onClick={handleRestoreSavedDashboardLayout}
                disabled={!hasSavedLayout}
                sx={{ py: 0.75 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <RestoreIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Restore saved layout"
                  secondary={hasSavedLayout ? 'Apply your last saved positions' : 'No saved layout yet'}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </MenuItem>
              <MenuItem dense onClick={handleResetDashboardLayout} sx={{ py: 0.75 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <RestartAltIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Default layout"
                  secondary="Built-in widget positions and sizes"
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </MenuItem>
            </Menu>
            <Box>
              <Typography
                variant="body2"
                component="div"
                sx={{ fontWeight: 400, lineHeight: 1.2, color: 'text.secondary', fontSize: '0.85rem' }}
              >
                {format(clockNow, 'M/d h:mm:ssa').toLowerCase()}
              </Typography>
              <Typography
                variant="caption"
                component="div"
                sx={{ color: connectionState === 'connected' ? 'success.main' : 'text.secondary' }}
              >
                Realtime: {connectionState}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          position: 'relative'
        }}
      >
        <ResponsiveGridLayout
          key={`dashboard-grid-${layoutResetNonce}`}
          layouts={gridLayouts}
          breakpoints={DASHBOARD_GRID_BREAKPOINTS}
          cols={DASHBOARD_GRID_COLS}
          rowHeight={26}
          margin={[8, 8]}
          containerPadding={[0, 0]}
          draggableHandle={`.${DASHBOARD_DRAG_HANDLE_CLASS}`}
          compactType="vertical"
          preventCollision={false}
          isDraggable
          isResizable
          onLayoutChange={handleDashboardLayoutChange}
        >
          {effectiveVisibility[DASHBOARD_WIDGET.activity] ? (
          <div key={DASHBOARD_WIDGET.activity} style={{ height: '100%' }}>
            <Paper
              sx={{
                p: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden'
              }}
            >
              <Box
                className={DASHBOARD_DRAG_HANDLE_CLASS}
                sx={{
                  px: 1.25,
                  py: 0.55,
                  backgroundColor: dashboardPalette.headerBg,
                  ...(dashboardPalette.showHeaderAccent !== false
                    ? { borderTop: `3px solid ${dashboardPalette.accent}` }
                    : {}),
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' }
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, letterSpacing: 0.2, color: dashboardPalette.headerText }}
                >
                  Recent activity
                </Typography>
              </Box>
            {loading ? (
              <Box py={1.5} display="flex" justifyContent="center">
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  px: 1,
                  py: 0.75,
                  fontFamily: '"Roboto Condensed", "Roboto", "Helvetica", "Arial", sans-serif'
                }}
              >
                {!activityFeed.length ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                    No recent activity yet.
                  </Typography>
                ) : (
                  activityFeed.map((item) => (
                    <Typography
                      key={item.id}
                      variant="body2"
                      component="div"
                      sx={{
                        fontSize: '0.76rem',
                        lineHeight: 1.35,
                        py: 0.2,
                        borderBottom: '1px solid rgba(224, 224, 224, 0.55)',
                        '&:last-of-type': { borderBottom: 'none' }
                      }}
                    >
                      <Box component="span" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                        {formatActivityStamp(item.created_at)}
                      </Box>
                      {' - '}
                      {renderActivityFeedMessage(item.message, item.links, item.emphasisSpan)}
                    </Typography>
                  ))
                )}
              </Box>
            )}
            </Paper>
          </div>
          ) : null}
          {effectiveVisibility[DASHBOARD_WIDGET.notifications] ? (
            <div key={DASHBOARD_WIDGET.notifications} style={{ height: '100%' }}>
              <DashboardNotificationsWidget
                headerDragClassName={DASHBOARD_DRAG_HANDLE_CLASS}
                loading={loading}
              />
            </div>
          ) : null}
          {effectiveVisibility[DASHBOARD_WIDGET.quotes] ? (
          <div key={DASHBOARD_WIDGET.quotes} style={{ height: '100%' }}>
            <Paper
              sx={{
                p: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden'
              }}
            >
              <Box
                className={DASHBOARD_DRAG_HANDLE_CLASS}
                sx={{
                  px: 1.25,
                  py: 0.55,
                  backgroundColor: dashboardPalette.headerBg,
                  ...(dashboardPalette.showHeaderAccent !== false
                    ? { borderTop: `3px solid ${dashboardPalette.accent}` }
                    : {}),
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' }
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2, color: dashboardPalette.headerText }}>
                  Quotes
                </Typography>
              </Box>
              {loading ? (
                <Box py={1.5} display="flex" justifyContent="center">
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: 1,
                    py: 0.75,
                    fontFamily: '"Roboto Condensed", "Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  {!recentQuoteActivity.length ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                      No recent quotes
                    </Typography>
                  ) : (
                    recentQuoteActivity.map((item) => (
                      <Typography
                        key={item.id}
                        variant="body2"
                        component="div"
                        onClick={() => navigate(`/quotes/${item.quote_id}`)}
                        sx={{
                          fontSize: '0.76rem',
                          lineHeight: 1.35,
                          py: 0.2,
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(224, 224, 224, 0.55)',
                          '&:last-of-type': { borderBottom: 'none' },
                          '&:hover': { backgroundColor: dashboardPalette.hoverBg }
                        }}
                      >
                        <Box component="span" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                          {formatActivityStamp(item.created_at)}
                        </Box>
                        {' - '}
                        {renderDashboardQuoteActivityLine(item)}
                      </Typography>
                    ))
                  )}
                </Box>
              )}
            </Paper>
          </div>
          ) : null}
          {effectiveVisibility[DASHBOARD_WIDGET.orders] ? (
          <div key={DASHBOARD_WIDGET.orders} style={{ height: '100%' }}>
            <Paper
              sx={{
                p: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden'
              }}
            >
              <Box
                className={DASHBOARD_DRAG_HANDLE_CLASS}
                sx={{
                  px: 1.25,
                  py: 0.55,
                  backgroundColor: dashboardPalette.headerBg,
                  ...(dashboardPalette.showHeaderAccent !== false
                    ? { borderTop: `3px solid ${dashboardPalette.accent}` }
                    : {}),
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' }
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2, color: dashboardPalette.headerText }}>
                  Orders
                </Typography>
              </Box>
              {loading ? (
                <Box py={1.5} display="flex" justifyContent="center">
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: 1,
                    py: 0.75,
                    fontFamily: '"Roboto Condensed", "Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  {!recentOrderActivity.length ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                      No recent orders
                    </Typography>
                  ) : (
                    recentOrderActivity.map((item) => (
                      <Typography
                        key={item.id}
                        variant="body2"
                        component="div"
                        onClick={() => navigate(`/work-orders/${item.work_order_id}`)}
                        sx={{
                          fontSize: '0.76rem',
                          lineHeight: 1.35,
                          py: 0.2,
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(224, 224, 224, 0.55)',
                          '&:last-of-type': { borderBottom: 'none' },
                          '&:hover': { backgroundColor: dashboardPalette.hoverBg }
                        }}
                      >
                        <Box component="span" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                          {formatActivityStamp(item.created_at)}
                        </Box>
                        {' - '}
                        {renderDashboardOrderActivityLine(item)}
                      </Typography>
                    ))
                  )}
                </Box>
              )}
            </Paper>
          </div>
          ) : null}
          {effectiveVisibility[DASHBOARD_WIDGET.shipments] ? (
          <div key={DASHBOARD_WIDGET.shipments} style={{ height: '100%' }}>
            <Paper
              sx={{
                p: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden'
              }}
            >
              <Box
                className={DASHBOARD_DRAG_HANDLE_CLASS}
                sx={{
                  px: 1.25,
                  py: 0.55,
                  backgroundColor: dashboardPalette.headerBg,
                  ...(dashboardPalette.showHeaderAccent !== false
                    ? { borderTop: `3px solid ${dashboardPalette.accent}` }
                    : {}),
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' }
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2, color: dashboardPalette.headerText }}>
                  Shipments Out
                </Typography>
              </Box>
              {loading ? (
                <Box py={1.5} display="flex" justifyContent="center">
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: 1,
                    py: 0.75,
                    fontFamily: '"Roboto Condensed", "Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  {!recentShipments.length ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                      No recent shipments
                    </Typography>
                  ) : (
                    recentShipments.map((s) => (
                      <Typography
                        key={s.id}
                        variant="body2"
                        component="div"
                        onClick={() => navigate(`/packing-slips/${s.id}`)}
                        sx={{
                          fontSize: '0.76rem',
                          lineHeight: 1.35,
                          py: 0.2,
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(224, 224, 224, 0.55)',
                          '&:last-of-type': { borderBottom: 'none' },
                          '&:hover': { backgroundColor: dashboardPalette.hoverBg }
                        }}
                      >
                        <Box component="span" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                          {formatActivityStamp(s.created_at)}
                        </Box>
                        {' - '}
                        {renderDashboardShipmentLine(s)}
                      </Typography>
                    ))
                  )}
                </Box>
              )}
            </Paper>
          </div>
          ) : null}
          {effectiveVisibility[DASHBOARD_WIDGET.purchaseOrders] ? (
          <div key={DASHBOARD_WIDGET.purchaseOrders} style={{ height: '100%' }}>
            <Paper
              sx={{
                p: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden'
              }}
            >
              <Box
                className={DASHBOARD_DRAG_HANDLE_CLASS}
                sx={{
                  px: 1.25,
                  py: 0.55,
                  backgroundColor: dashboardPalette.headerBg,
                  ...(dashboardPalette.showHeaderAccent !== false
                    ? { borderTop: `3px solid ${dashboardPalette.accent}` }
                    : {}),
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' }
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2, color: dashboardPalette.headerText }}>
                  Purchase Orders
                </Typography>
              </Box>
              {loading ? (
                <Box py={1.5} display="flex" justifyContent="center">
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: 1,
                    py: 0.75,
                    fontFamily: '"Roboto Condensed", "Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  {!recentPurchaseOrderActivity.length ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                      No recent purchase orders
                    </Typography>
                  ) : (
                    recentPurchaseOrderActivity.map((item) => (
                      <Typography
                        key={item.id}
                        variant="body2"
                        component="div"
                        onClick={() => navigate(`/procurement/po/${item.purchase_order_id}`)}
                        sx={{
                          fontSize: '0.76rem',
                          lineHeight: 1.35,
                          py: 0.2,
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(224, 224, 224, 0.55)',
                          '&:last-of-type': { borderBottom: 'none' },
                          '&:hover': { backgroundColor: dashboardPalette.hoverBg }
                        }}
                      >
                        <Box component="span" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                          {formatActivityStamp(item.created_at)}
                        </Box>
                        {' - '}
                        {renderDashboardPurchaseOrderActivityLine(item)}
                      </Typography>
                    ))
                  )}
                </Box>
              )}
            </Paper>
          </div>
          ) : null}
          {effectiveVisibility[DASHBOARD_WIDGET.received] ? (
          <div key={DASHBOARD_WIDGET.received} style={{ height: '100%' }}>
            <Paper
              sx={{
                p: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden'
              }}
            >
              <Box
                className={DASHBOARD_DRAG_HANDLE_CLASS}
                sx={{
                  px: 1.25,
                  py: 0.55,
                  backgroundColor: dashboardPalette.headerBg,
                  ...(dashboardPalette.showHeaderAccent !== false
                    ? { borderTop: `3px solid ${dashboardPalette.accent}` }
                    : {}),
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' }
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2, color: dashboardPalette.headerText }}>
                  Shipments in
                </Typography>
              </Box>
              {loading ? (
                <Box py={1.5} display="flex" justifyContent="center">
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: 1,
                    py: 0.75,
                    fontFamily: '"Roboto Condensed", "Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  {!recentReceivedShipmentActivity.length ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                      No received shipments
                    </Typography>
                  ) : (
                    recentReceivedShipmentActivity.map((item) => (
                      <Typography
                        key={item.id}
                        variant="body2"
                        component="div"
                        onClick={() => navigate(`/procurement/shipments-in/${item.shipment_in_id}`)}
                        sx={{
                          fontSize: '0.76rem',
                          lineHeight: 1.35,
                          py: 0.2,
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(224, 224, 224, 0.55)',
                          '&:last-of-type': { borderBottom: 'none' },
                          '&:hover': { backgroundColor: dashboardPalette.hoverBg }
                        }}
                      >
                        <Box component="span" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                          {formatActivityStamp(item.created_at)}
                        </Box>
                        {' - '}
                        {renderDashboardShipmentInActivityLine(item)}
                      </Typography>
                    ))
                  )}
                </Box>
              )}
            </Paper>
          </div>
          ) : null}
          {effectiveVisibility[DASHBOARD_WIDGET.vendorInvoices] ? (
          <div key={DASHBOARD_WIDGET.vendorInvoices} style={{ height: '100%' }}>
            <Paper
              sx={{
                p: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden'
              }}
            >
              <Box
                className={DASHBOARD_DRAG_HANDLE_CLASS}
                sx={{
                  px: 1.25,
                  py: 0.55,
                  backgroundColor: dashboardPalette.headerBg,
                  ...(dashboardPalette.showHeaderAccent !== false
                    ? { borderTop: `3px solid ${dashboardPalette.accent}` }
                    : {}),
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' }
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2, color: dashboardPalette.headerText }}>
                  AP Invoices
                </Typography>
              </Box>
              {loading ? (
                <Box py={1.5} display="flex" justifyContent="center">
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: 1,
                    py: 0.75,
                    fontFamily: '"Roboto Condensed", "Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  {!recentVendorInvoiceActivity.length ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                      No recent vendor invoices
                    </Typography>
                  ) : (
                    recentVendorInvoiceActivity.map((item) => (
                      <Box
                        key={item.id}
                        onClick={() =>
                          navigate(`/procurement/invoices?invoice=${item.vendor_invoice_id}`)
                        }
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 0.75,
                          fontSize: '0.76rem',
                          lineHeight: 1.35,
                          py: 0.2,
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(224, 224, 224, 0.55)',
                          '&:last-of-type': { borderBottom: 'none' },
                          '&:hover': { backgroundColor: dashboardPalette.hoverBg }
                        }}
                      >
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 'inherit',
                            lineHeight: 'inherit',
                            fontFamily: 'inherit'
                          }}
                        >
                          <Box component="span" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                            {formatActivityStamp(item.created_at)}
                          </Box>
                          {' - '}
                          {renderDashboardVendorInvoiceActivityLine(item)}
                        </Typography>
                        <Box
                          sx={{
                            flexShrink: 0,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            pt: 0.1,
                            minWidth: 88
                          }}
                        >
                          {renderDashboardApInvoiceStatusChip(item)}
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              )}
            </Paper>
          </div>
          ) : null}
          {effectiveVisibility[DASHBOARD_WIDGET.invoices] ? (
          <div key={DASHBOARD_WIDGET.invoices} style={{ height: '100%' }}>
            <DashboardEventTable
              title="AR Invoices"
              items={recentInvoices}
              emptyText="No recent invoices"
              loading={loading}
              showColumnHeaders
              tableLayout="auto"
              headerDragClassName={DASHBOARD_DRAG_HANDLE_CLASS}
              onRowClick={(inv) => navigate(`/customer-invoices/${inv.id}`)}
            columns={[
              {
                key: 'date',
                header: 'Date/Time',
                width: '1%',
                minWidth: 108,
                noTruncate: true,
                hideBorderRight: true,
                headerCellSx: { pr: '5px' },
                cellSx: { pr: '5px' },
                render: (inv) => {
                  const stamp = inv.updated_at || inv.created_at || inv.invoice_date;
                  return (
                    <Box component="span" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                      {formatActivityStamp(stamp)}
                    </Box>
                  );
                }
              },
              {
                key: 'inv',
                header: 'Invoice',
                width: '1%',
                headerCellSx: { pl: 0, pr: '35px' },
                cellSx: { pl: 0, pr: '35px' },
                render: (inv) => {
                  const label = inv.identifier || inv.invoice_number || '-';
                  if (label === '-') return label;
                  return (
                    <Link
                      component={RouterLink}
                      to={`/customer-invoices/${inv.id}`}
                      underline="hover"
                      onClick={(e) => e.stopPropagation()}
                      sx={orderLineLinkSx}
                    >
                      {label}
                    </Link>
                  );
                }
              },
              {
                key: 'order',
                header: 'Order',
                minWidth: 72,
                headerCellSx: { pl: 0 },
                cellSx: { pl: 0 },
                render: (inv) => {
                  const label = inv.work_order_identifier || inv.work_order_number || '-';
                  const to = inv.work_order_id ? `/work-orders/${inv.work_order_id}` : null;
                  if (!to || label === '-') return label;
                  return (
                    <Link
                      component={RouterLink}
                      to={to}
                      underline="hover"
                      onClick={(e) => e.stopPropagation()}
                      sx={orderLineLinkSx}
                    >
                      {label}
                    </Link>
                  );
                }
              },
              { key: 'customer', header: 'Customer', minWidth: 100, render: (inv) => customerName(inv) },
              {
                key: 'total',
                header: 'Inv Total',
                headerAlign: 'right',
                minWidth: 72,
                render: (inv) => (
                  <Box component="span" sx={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatCurrency(inv.total_amount)}
                  </Box>
                )
              },
              {
                key: 'balance',
                header: 'Balance',
                headerAlign: 'right',
                minWidth: 68,
                render: (inv) => (
                  <Box component="span" sx={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatCurrency(invoiceBalanceDue(inv))}
                  </Box>
                )
              },
              {
                key: 'status',
                header: 'Status',
                minWidth: 88,
                noTruncate: true,
                render: (inv) => renderInvoiceStatusChips(inv)
              }
            ]}
              palette={dashboardPalette}
            />
          </div>
          ) : null}
        </ResponsiveGridLayout>
      </Box>
    </Box>
  );
};

export default Dashboard;
